import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, FileText, IndianRupee } from "lucide-react";
import { Kicker, Reveal } from "@/components/Motion";
import ServiceCard from "@/components/ServiceCard";
import { SERVICES, serviceBySlug } from "@/data/services";

export default function ServiceDetails() {
  const { slug } = useParams();
  const service = serviceBySlug(slug);

  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid="service-details-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <Link
            to="/services"
            data-testid="service-back-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-blue-900"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            All services
          </Link>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-blue-50 text-blue-900">
              <Icon className="h-7 w-7" aria-hidden="true" />
            </span>
            <Kicker>Service {service.num} of 13</Kicker>
          </div>
          <h1
            className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl"
            data-testid="service-title"
          >
            {service.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
            {service.desc}
          </p>
        </div>
      </header>

      <section className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8">
        <div className="space-y-6 lg:col-span-7">
          <Reveal>
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
                Eligibility
              </h2>
              <ul className="mt-4 space-y-3">
                {service.eligibility.map((e) => (
                  <li key={e} className="flex items-start gap-3 text-sm leading-relaxed text-slate-700">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
                    {e}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
                Required documents
              </h2>
              <ul className="mt-4 space-y-3">
                {service.documents.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm leading-relaxed text-slate-700">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-blue-900" aria-hidden="true" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.12} className="lg:col-span-5">
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8 lg:sticky lg:top-24">
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              Booking fee
            </p>
            <p className="mt-3 flex items-center gap-1 font-display text-3xl font-bold tracking-tight text-slate-900">
              {service.fee > 0 ? (
                <>
                  <IndianRupee className="h-7 w-7" aria-hidden="true" />
                  {service.fee}
                </>
              ) : (
                "Free"
              )}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              {service.fee > 0
                ? "Payable online via Razorpay after booking, or at the service desk."
                : "This service is provided free of charge."}
            </p>
            <Link
              to={`/book?service=${service.slug}`}
              data-testid="service-book-now-button"
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-900 px-6 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-blue-800"
            >
              Book Now
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
              Track status anytime from your account
            </p>
          </div>
        </Reveal>
      </section>

      <section className="border-t border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">
              You may also need
            </h2>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {related.map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
